import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, LogOut, Trash2, CalendarPlus, CheckCircle2, Clock, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { useSEO } from "../lib/seo";
import { authApi, clearToken, getToken } from "../lib/auth";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleString("es-ES", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
};

const STATUS = {
  pendiente: { label: "Pendiente", cls: "bg-bg-secondary text-ink-muted" },
  confirmada: { label: "Confirmada", cls: "bg-bg-blue text-sage-dark" },
  atendida: { label: "Atendida", cls: "bg-bg-sage text-sage-dark" },
  cancelada: { label: "Cancelada", cls: "bg-red-50 text-red-600" },
};

export default function AdminPanel() {
  useSEO({ title: "Panel · Victoria Fuentes", description: "Panel privado.", path: "/panel", noindex: true });
  const navigate = useNavigate();
  const [tab, setTab] = useState("citas");
  const [appts, setAppts] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ patient_name: "", patient_email: "", datetime_iso: "", notes: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const api = authApi();
      const [a, c] = await Promise.all([api.get("/appointments"), api.get("/contact")]);
      setAppts(a.data);
      setContacts(c.data);
    } catch (err) {
      if (err.response?.status === 401) { clearToken(); navigate("/acceso"); }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!getToken()) { navigate("/acceso"); return; }
    load();
  }, [load, navigate]);

  const addAppt = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await authApi().post("/appointments", form);
      toast.success("Cita añadida. El recordatorio se enviará el día antes por la mañana.");
      setForm({ patient_name: "", patient_email: "", datetime_iso: "", notes: "" });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo guardar la cita.");
    } finally {
      setSaving(false);
    }
  };

  const delAppt = async (id) => {
    try {
      await authApi().delete(`/appointments/${id}`);
      setAppts((prev) => prev.filter((a) => a.id !== id));
      toast.success("Cita eliminada.");
    } catch {
      toast.error("No se pudo eliminar.");
    }
  };

  const updateStatus = async (id, status) => {
    const prev = appts;
    setAppts((p) => p.map((a) => (a.id === id ? { ...a, status } : a)));
    try {
      await authApi().patch(`/appointments/${id}`, { status });
    } catch {
      setAppts(prev);
      toast.error("No se pudo actualizar el estado.");
    }
  };

  const logout = () => { clearToken(); navigate("/acceso"); };

  const inputCls = "w-full rounded-2xl border border-ink/10 bg-white px-4 py-3 text-sm text-ink placeholder:text-ink-muted focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30";

  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-bg-secondary"><Loader2 className="h-8 w-8 animate-spin text-sage-dark" /></div>;
  }

  return (
    <div className="min-h-screen bg-bg-secondary" data-testid="admin-panel">
      <header className="border-b border-ink/10 bg-bg-primary">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
          <div>
            <p className="font-serif text-2xl text-ink">Panel de Victoria Fuentes</p>
            <p className="text-xs text-ink-muted">Gestión de citas y mensajes</p>
          </div>
          <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-ink/20 px-5 py-2.5 text-sm text-ink transition-colors hover:bg-ink/5">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Salir
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="mb-8 flex gap-2">
          <button onClick={() => setTab("citas")} data-testid="tab-citas" className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors ${tab === "citas" ? "bg-ink text-bg-primary" : "border border-ink/15 text-ink hover:bg-ink/5"}`}>
            Citas ({appts.length})
          </button>
          <button onClick={() => setTab("mensajes")} data-testid="tab-mensajes" className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors ${tab === "mensajes" ? "bg-ink text-bg-primary" : "border border-ink/15 text-ink hover:bg-ink/5"}`}>
            Mensajes ({contacts.length})
          </button>
        </div>

        {tab === "citas" && (
          <div className="grid gap-8 lg:grid-cols-5">
            <form onSubmit={addAppt} data-testid="appointment-form" className="lg:col-span-2 h-fit rounded-4xl border border-ink/10 bg-bg-primary p-6">
              <p className="mb-4 flex items-center gap-2 font-serif text-xl text-ink"><CalendarPlus className="h-5 w-5 text-sage-dark" strokeWidth={1.5} /> Nueva cita</p>
              <div className="space-y-3">
                <input required value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} placeholder="Nombre del paciente" data-testid="appt-name" className={inputCls} />
                <input required type="email" value={form.patient_email} onChange={(e) => setForm({ ...form, patient_email: e.target.value })} placeholder="Email del paciente" data-testid="appt-email" className={inputCls} />
                <label className="block text-xs text-ink-muted">Fecha y hora de la cita
                  <input required type="datetime-local" value={form.datetime_iso} onChange={(e) => setForm({ ...form, datetime_iso: e.target.value })} data-testid="appt-datetime" className={`${inputCls} mt-1`} />
                </label>
                <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notas (opcional)" rows={3} data-testid="appt-notes" className={inputCls} />
              </div>
              <button type="submit" disabled={saving} data-testid="appt-submit" className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-ink px-6 py-3.5 text-sm font-medium text-bg-primary transition-colors hover:bg-sage-dark disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Guardar cita
              </button>
              <p className="mt-3 text-xs text-ink-muted">El recordatorio se envía automáticamente al paciente por email el día anterior por la mañana.</p>
            </form>

            <div className="lg:col-span-3 space-y-3" data-testid="appointments-list">
              {appts.length === 0 && <p className="rounded-3xl border border-dashed border-ink/15 p-8 text-center text-sm text-ink-muted">Aún no hay citas registradas.</p>}
              {appts.map((a) => (
                <div key={a.id} data-testid={`appt-item-${a.id}`} className="flex items-start justify-between gap-4 rounded-3xl border border-ink/10 bg-bg-primary p-5">
                  <div className="min-w-0">
                    <p className="font-medium text-ink">{a.patient_name}</p>
                    <p className="mt-1 flex items-center gap-2 text-sm text-ink-soft"><Clock className="h-4 w-4 text-sage-dark" strokeWidth={1.5} /> {fmt(a.datetime_iso)}</p>
                    <p className="text-xs text-ink-muted">{a.patient_email}</p>
                    {a.notes && <p className="mt-2 text-sm text-ink-soft">{a.notes}</p>}
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${(STATUS[a.status] || STATUS.pendiente).cls}`} data-testid={`appt-status-badge-${a.id}`}>
                        {(STATUS[a.status] || STATUS.pendiente).label}
                      </span>
                      <select
                        value={a.status || "pendiente"}
                        onChange={(e) => updateStatus(a.id, e.target.value)}
                        data-testid={`appt-status-select-${a.id}`}
                        aria-label="Cambiar estado de la cita"
                        className="rounded-full border border-ink/15 bg-white px-3 py-1 text-xs text-ink focus:border-sage focus:outline-none focus:ring-2 focus:ring-sage/30"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="confirmada">Confirmada</option>
                        <option value="atendida">Atendida</option>
                        <option value="cancelada">Cancelada</option>
                      </select>
                      <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${a.reminder_sent ? "bg-bg-sage text-sage-dark" : "bg-bg-secondary text-ink-muted"}`}>
                        {a.reminder_sent ? <><CheckCircle2 className="h-3 w-3" /> Recordatorio enviado</> : <><Clock className="h-3 w-3" /> Recordatorio pendiente</>}
                      </span>
                    </div>
                  </div>
                  <button onClick={() => delAppt(a.id)} data-testid={`appt-delete-${a.id}`} aria-label="Eliminar cita" className="rounded-full p-2 text-ink-muted transition-colors hover:bg-red-50 hover:text-red-600">
                    <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {tab === "mensajes" && (
          <div className="space-y-3" data-testid="messages-list">
            {contacts.length === 0 && <p className="rounded-3xl border border-dashed border-ink/15 p-8 text-center text-sm text-ink-muted">Aún no hay mensajes recibidos.</p>}
            {contacts.map((c) => (
              <div key={c.id} className="rounded-3xl border border-ink/10 bg-bg-primary p-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="font-medium text-ink">{c.name}</p>
                  <p className="text-xs text-ink-muted">{fmt(c.created_at)}</p>
                </div>
                <div className="mt-2 flex flex-wrap gap-4 text-sm text-ink-soft">
                  <a href={`mailto:${c.email}`} className="flex items-center gap-1.5 hover:text-sage-dark"><Mail className="h-4 w-4" strokeWidth={1.5} /> {c.email}</a>
                  <a href={`tel:${c.phone}`} className="flex items-center gap-1.5 hover:text-sage-dark"><Phone className="h-4 w-4" strokeWidth={1.5} /> {c.phone}</a>
                </div>
                <p className="mt-3 rounded-2xl bg-bg-secondary p-4 text-sm text-ink-soft">{c.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
